'use client';

import classNames from 'classnames';
import { PaddingOptions } from 'mapbox-gl';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import PublicLandsOverlay from '@alltrails/advanced-map-layers/components/PublicLandsOverlay';
import logExploreMapDistanceAwayDropdownClicked from '@alltrails/amplitude/events/logExploreMapDistanceAwayDropdownClicked';
import logMapViewAdjustmentClicked from '@alltrails/amplitude/events/logMapViewAdjustmentClicked';
import ConditionOverlays from '@alltrails/conditions/components/ConditionOverlays';
import ConditionsUpsellModal from '@alltrails/conditions/components/ConditionsUpsellModal';
import useConditionsValidation from '@alltrails/conditions/hooks/useConditionsValidation';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useUser from '@alltrails/context/hooks/useUser';
import IconButton from '@alltrails/denali/components/IconButton';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Print from '@alltrails/denali/icons/Print';
import { useExperiments } from '@alltrails/experiments';
import ExploreFiltersBar from '@alltrails/explore-service/components/ExploreFiltersBar/ExploreFiltersBar';
import { updateBoundingBox } from '@alltrails/explore-service/redux/exploreServiceSlice';
import getMapBoundingBox from '@alltrails/explore-service/utils/getMapBoundingBox';
import CommunityHeatmap from '@alltrails/heatmaps/components/CommunityHeatmap';
import HeatmapErrorToast from '@alltrails/heatmaps/components/HeatmapErrorToast';
import HeatmapFilterModal from '@alltrails/heatmaps/components/HeatmapFilterModal';
import HeatmapsUpsellModal from '@alltrails/heatmaps/components/HeatmapsUpsellModal';
import useHeatmapsPeakValidation from '@alltrails/heatmaps/hooks/useHeatmapsPeakValidation';
import BaseMap from '@alltrails/maps/components/BaseMap/BaseMap';
import CommunityActivity from '@alltrails/maps/components/CommunityActivity/CommunityActivity';
import DistanceAwayCenterMarker from '@alltrails/maps/components/DistanceAwayCenterMarker/DistanceAwayCenterMarker';
import { dynamicStyleImages } from '@alltrails/maps/components/DynamicStyleOverlays/index';
import Isochrone from '@alltrails/maps/components/Isochrone/Isochrone';
import MapAttribution from '@alltrails/maps/components/MapAttribution/MapAttribution';
import MapLayout from '@alltrails/maps/components/MapLayout/MapLayout';
import Photospots from '@alltrails/maps/components/Photospots/Photospots';
import useCurrentLocationButton from '@alltrails/maps/hooks/useCurrentLocationButton';
import useMap from '@alltrails/maps/hooks/useMap';
import usePrintUrlSearchParams from '@alltrails/maps/hooks/usePrintUrlSearchParams';
import { updateMapZoomBucket } from '@alltrails/maps/redux/reducer';
import { defaultSupportedDynamicStyleIds } from '@alltrails/maps/utils/constants';
import getZoomBucket from '@alltrails/maps/utils/getZoomBucket';
import type { FeatureEntitlement } from '@alltrails/types/FeatureEntitlement';
import SignUpModal from '@alltrails/user-auth/components/SignUpModal';
import useExploreConditions from '@/hooks/useExploreConditions';
import useIsPage from '@/hooks/useIsPage';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsShowingMobileList, setMapPadding } from '@/utils/slices/exploreSlice';
import BuildCustomRouteButton from '../BuildCustomRouteButton/BuildCustomRouteButton';
import ExploreConditionsMobileWarning from '../ExploreConditionsMobileWarning/ExploreConditionsMobileWarning';
import MapCenterMarker from '../MapCenterMarker/MapCenterMarker';
import MobileViewToggle from '../MobileViewToggle/MobileViewToggle';
import StreetAddressMapMarker from '../StreetAddressMapMarker/StreetAddressMapMarker';
import UnclusteringDebugInfo from '../UnclusteringDebugInfo/UnclusteringDebugInfo';
import styles from './ExploreMap.module.scss';
import MapControls from './MapControls';
import MapLegends from './MapLegends';

const images = dynamicStyleImages;

const onCurrentLocationButtonClick = () => logMapViewAdjustmentClicked({ action_taken: 'view current location clicked' });

type ExploreMapProps = {
  bottomCenterContent?: ReactNode;
  children?: ReactNode;
  mapComponents?: ReactNode;
  mobileLocationCTA?: ReactNode;
  sidebar?: ReactNode;
  topLeftContent?: ReactNode;
};

const ExploreMap = ({ bottomCenterContent, children, mapComponents, mobileLocationCTA, sidebar, topLeftContent }: ExploreMapProps) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const user = useUser();
  const experiments = useExperiments();
  const debugEnabled = experiments?.['mugen-explore-debug']?.value === 'on';
  const communityActivityEnabled = experiments?.['web-disco-rm-community-activity']?.value === 'on';
  const hasVectorOverlaysExperiment = experiments?.['web-discover-vector-overlays-rm']?.value === 'on';

  const { hasPermission } = useAuthorization();
  const hasTrailsManagePermission = hasPermission({ permission: 'trails:manage' });
  const { isCustomRoute, isSavedCustomRoute, isExplorePOI, isExplorePark, isExploreRecentlyViewed, isExploreRoot, isMembersCustomRoute } =
    useIsPage();
  const getPrintUrlSearchParams = usePrintUrlSearchParams();
  const isMobile = useIsMobileSizedScreen();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);

  const {
    showFilterBar,
    initialMapBounds,
    initialMapCenter,
    isMobileListAtTop,
    headerHeight,
    openDistanceAwayOnNewLocationResult,
    panelCollapsed,
    distanceAway,
    distanceAwayIsochrone,
    dynamicStyleIds,
    isHeatmapFilterModalOpen
  } = useAppSelector(state => ({
    showFilterBar: state.explore.showFilterBar,
    initialMapBounds: state.explore.initialMapBounds,
    initialMapCenter: state.explore.initialMapCenter,
    isMobileListAtTop: state.explore.isMobileListAtTop,
    headerHeight: state.explore.headerHeight,
    openDistanceAwayOnNewLocationResult: state.explore.openDistanceAwayOnNewLocationResult,
    panelCollapsed: state.explore.panelCollapsed,
    distanceAway: state.exploreService.distanceAway,
    distanceAwayIsochrone: state.exploreService.distanceAwayIsochrone,
    dynamicStyleIds: state.map.dynamicStyleIds,
    isHeatmapFilterModalOpen: state.heatmaps.isHeatmapFilterModalOpen
  }));

  const { showHeatmapsUpsellModal, onHeatmapsUpsellModalCloseRequest, handleHeatmapFailedEntitlementCheck, removeAppliedHeatmaps } =
    useHeatmapsPeakValidation();

  const { showConditionsUpsellModal, onConditionsUpsellModalCloseRequest, handleConditionsFailedEntitlementCheck, removeAppliedConditions } =
    useConditionsValidation();

  const pageSupportsConditionOverlays = !isMobile && (isExplorePOI || isExploreRecentlyViewed || isExploreRoot || isExplorePark);
  useExploreConditions();

  const pageSupportsCommunityActivity = communityActivityEnabled && !isMembersCustomRoute;
  const supportedDynamicStyleIds = useMemo(() => {
    const ids = [...defaultSupportedDynamicStyleIds];

    if (!hasTrailsManagePermission && pageSupportsCommunityActivity) {
      ids.push('communityActivity');
    }

    // Show photospots for admins on /explore
    if (hasTrailsManagePermission && isExploreRoot) {
      ids.push('photospot');
    }

    if (hasVectorOverlaysExperiment) {
      ids.push('publicLands');
    }

    if (pageSupportsConditionOverlays) {
      ids.push('precipitation', 'temperature', 'snowDepth');
    }
    return ids;
  }, [hasTrailsManagePermission, isExploreRoot, pageSupportsConditionOverlays, pageSupportsCommunityActivity, hasVectorOverlaysExperiment]);

  const handleFailedEntitlementCheck = useCallback(
    (entitlement: FeatureEntitlement, applyStyle: () => void) => {
      if (entitlement === 'advanced_conditions') {
        handleConditionsFailedEntitlementCheck(applyStyle);
      } else if (entitlement === 'community_heat_maps') {
        handleHeatmapFailedEntitlementCheck(applyStyle);
      }
    },
    [handleConditionsFailedEntitlementCheck, handleHeatmapFailedEntitlementCheck]
  );

  const [shouldRemoveHeatmaps, setShouldRemoveHeatmaps] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (showHeatmapsUpsellModal) {
      timer = setTimeout(() => {
        // This is to prevent non-peak users from removing the upsells from the DOM
        setShouldRemoveHeatmaps(true);
      }, 1000);
    } else {
      setShouldRemoveHeatmaps(false);
    }

    return () => clearTimeout(timer);
  }, [showHeatmapsUpsellModal]);

  const [shouldRemoveConditions, setShouldRemoveConditions] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (showConditionsUpsellModal) {
      timer = setTimeout(() => {
        // This is to prevent non-peak users from removing the upsells from the DOM
        setShouldRemoveConditions(true);
      }, 1000);
    } else {
      setShouldRemoveConditions(false);
    }

    return () => clearTimeout(timer);
  }, [showConditionsUpsellModal]);

  const handleMapMoveEnd = useCallback(
    (zoom: number) => {
      const zoomBucket = getZoomBucket(zoom);
      dispatch(updateMapZoomBucket(zoomBucket));
      if (shouldRemoveHeatmaps) removeAppliedHeatmaps();
      if (shouldRemoveConditions) removeAppliedConditions();
    },
    [dispatch, shouldRemoveHeatmaps, removeAppliedHeatmaps, shouldRemoveConditions, removeAppliedConditions]
  );

  const redirectToPrintUrl = useCallback(() => {
    const printUrl = window.location.pathname
      .replace('/custom-routes/', '/map/')
      .replace(/\?.*$/, '')
      .concat(`/print?${getPrintUrlSearchParams().toString()}`);

    window.location.assign(printUrl);
  }, [getPrintUrlSearchParams]);

  const onPrintCLick = useCallback(() => {
    if (!user) {
      setIsSignUpModalOpen(true);
      return;
    }
    redirectToPrintUrl();
  }, [redirectToPrintUrl, user]);

  const map = useMap();

  const onMapPaddingChange = useCallback(
    (newPadding: PaddingOptions) => {
      dispatch(setMapPadding(newPadding));
      if (map) {
        dispatch(updateBoundingBox(getMapBoundingBox(map)));
      }
    },
    [dispatch, map]
  );

  const [showDistanceAwayMarker, setShowDistanceAwayMarker] = useState(false);
  const onDistanceAwayFilterClose = useCallback(() => {
    setShowDistanceAwayMarker(false);
  }, []);
  const onDistanceAwayFilterOpen = useCallback(() => {
    logExploreMapDistanceAwayDropdownClicked();
    setShowDistanceAwayMarker(true);
    // Kick users to the map view since the DA filter is much more while viewing the map
    dispatch(setIsShowingMobileList(false));
  }, [dispatch]);

  const renderMapOverlayChildren = useCallback(
    (mapPadding: PaddingOptions) => {
      // Only show the marker when a distance away filter isn't already applied.
      // When one is already applied, we don't reset the center point, so don't need the marker visible.

      return !distanceAway && showDistanceAwayMarker ? (
        <div
          className={styles.mapArea}
          style={{
            top: mapPadding.top ?? 0,
            left: mapPadding.left ?? 0,
            bottom: mapPadding.bottom ?? 0,
            right: mapPadding.right ?? 0
          }}
        >
          <MapCenterMarker className={styles.mapCenterMarker} />
        </div>
      ) : null;
    },
    [distanceAway, showDistanceAwayMarker]
  );

  const { currentLocationMarker, currentLocationButton, currentLocationModal } = useCurrentLocationButton(onCurrentLocationButtonClick);

  // The sidebar slot actually renders the mobile results list on mobile
  const desktopSidebar = isMobile ? undefined : sidebar;
  const mobileResultsList = isMobile ? sidebar : undefined;

  const mapLayoutTopLeftContent =
    showFilterBar && !isMobile ? (
      <ExploreFiltersBar
        map={map}
        onDistanceAwayFilterClose={onDistanceAwayFilterClose}
        onDistanceAwayFilterOpen={onDistanceAwayFilterOpen}
        openDistanceAwayOnNewLocationResult={openDistanceAwayOnNewLocationResult}
      />
    ) : (
      topLeftContent
    );

  if (isMobile && (isCustomRoute || isMembersCustomRoute)) {
    return;
  }

  return (
    <>
      <div
        className={styles.container}
        // Note that changes to this style seem trigger an async map resize operation
        // This means that effects listening to changes to headerHeight from the redux store may be triggered before the map has been resized
        style={!!headerHeight ? { height: `calc(100dvh - ${headerHeight}px)` } : {}}
      >
        {isMobile && showFilterBar && (
          <div className={classNames(styles.mobileFilterBar, { [styles.shadow]: !isMobileListAtTop })}>
            <ExploreFiltersBar
              filtersContainerClassName={styles.mobileFiltersContainer}
              map={map}
              onDistanceAwayFilterClose={onDistanceAwayFilterClose}
              onDistanceAwayFilterOpen={onDistanceAwayFilterOpen}
              openDistanceAwayOnNewLocationResult={openDistanceAwayOnNewLocationResult}
            />
          </div>
        )}
        {(initialMapBounds || initialMapCenter) && (
          <div className={styles.mapContainer}>
            <BaseMap
              disableDefaultAttribution
              initialBounds={initialMapBounds}
              initialCenter={initialMapCenter}
              images={images}
              preventReuseMaps
              supportedDynamicStyleIds={supportedDynamicStyleIds}
              onMoveEnd={({ zoom }) => handleMapMoveEnd(zoom)}
            >
              {distanceAwayIsochrone ? <Isochrone polygon={distanceAwayIsochrone} /> : null}
              {distanceAwayIsochrone && distanceAway ? (
                <DistanceAwayCenterMarker latitude={distanceAway.center.lat} longitude={distanceAway.center.lng} />
              ) : null}
              <StreetAddressMapMarker />
              {pageSupportsConditionOverlays && <ConditionOverlays />}
              <CommunityHeatmap />
              {hasVectorOverlaysExperiment && dynamicStyleIds?.includes('publicLands') && <PublicLandsOverlay />}
              {communityActivityEnabled && dynamicStyleIds?.includes('communityActivity') && <CommunityActivity />}
              {currentLocationMarker}
              {dynamicStyleIds?.includes('photospot') && <Photospots />}
              <HeatmapErrorToast />
              <MapAttribution />
              {mapComponents}
            </BaseMap>
            <MapLayout
              bottomCenterContent={bottomCenterContent}
              legends={
                <>
                  {isMobile && (
                    <div className={styles.mobileCTAs}>
                      <MobileViewToggle />
                      {mobileLocationCTA}
                    </div>
                  )}
                  <MapLegends />
                </>
              }
              mapControls={
                <MapControls
                  currentLocationButton={currentLocationButton}
                  handleFailedEntitlementCheck={handleFailedEntitlementCheck}
                  pauseFocusTrap={showHeatmapsUpsellModal || showConditionsUpsellModal}
                />
              }
              onMapPaddingChange={onMapPaddingChange}
              pageControls={
                <>
                  {isExploreRoot ? <BuildCustomRouteButton /> : null}
                  {isExploreRoot && debugEnabled && <UnclusteringDebugInfo />}
                  {isSavedCustomRoute ? (
                    <IconButton
                      icon={{ Component: Print }}
                      onClick={onPrintCLick}
                      title={intl.formatMessage({ defaultMessage: 'Print' })}
                      variant="elevated"
                    />
                  ) : null}
                  {debugEnabled && map && (
                    <div className={styles.debugInfo}>
                      <span>Zoom: {Math.round(map.getZoom() * 10000) / 10000}</span>
                    </div>
                  )}
                </>
              }
              renderChildren={renderMapOverlayChildren}
              sidebar={desktopSidebar}
              sidebarCollapsed={panelCollapsed}
              topLeftContent={mapLayoutTopLeftContent}
            />
            {isMobile && (
              <div className={classNames(styles.mobileMapOverlays, { [styles.isShowingMobileList]: Boolean(mobileResultsList) })}>
                {mobileResultsList}
              </div>
            )}
          </div>
        )}
        {children}
      </div>
      {currentLocationModal}
      {isHeatmapFilterModalOpen && <HeatmapFilterModal />}
      <HeatmapsUpsellModal
        isOpen={showHeatmapsUpsellModal}
        onCloseRequest={onHeatmapsUpsellModalCloseRequest}
        upsellTrigger="community heatmaps layer tapped explore map"
      />
      <ConditionsUpsellModal
        isOpen={showConditionsUpsellModal}
        onCloseRequest={onConditionsUpsellModalCloseRequest}
        upsellTrigger="advanced conditions layer tapped"
      />
      <ExploreConditionsMobileWarning isMobile={isMobile} />
      {isSignUpModalOpen && (
        <SignUpModal
          isOpen={isSignUpModalOpen}
          closeModal={() => setIsSignUpModalOpen(false)}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            redirectToPrintUrl();
          }}
          trigger="Print Map"
          upsellTrigger="Print Map"
        />
      )}
    </>
  );
};

export default ExploreMap;
